import React from 'react';

import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import coreObjectIsArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import * as FileUtil from 'folder-items/FileUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

import {ArchiveModalItem, ArchiveOptionsForModal} from '../modals/ArchiveModal';
import ArchiveButton from './ArchiveButton';
import UnarchiveButton from './UnarchiveButton';

interface ArchiveButtonProps {
  className?: string;
  fullWidth: boolean;
  showText: boolean;
  buttonStyle: ButtonStyle;
  items: Array<ArchiveModalItem>;
  onArchive: (itemIds: Array<string>, purpose: string) => void;
  tooltipTitle?: string;
  getAnalyticsMetadata?: () => AnalyticsMetadataForContext;
}

export interface ArchiveOrUnarchiveButtonProps {
  archiveButtonClass?: React.ComponentType<ArchiveButtonProps>;
  archiveTooltipTitle?: string;
  unarchiveTooltipTitle?: string;
  buttonStyle?: ButtonStyle;
  fullWidth?: boolean;
  className?: string;
  isArchivable?: boolean;
  isUnarchivable?: boolean;
  item: ArchiveModalItem;
  onArchive: (itemIds: Array<string>, purpose: string, options?: ArchiveOptionsForModal) => void;
  onUnarchive: (itemIds: Array<string>) => void;
  showText?: boolean;
  getAnalyticsMetadata?: () => AnalyticsMetadataForContext;
}

export default function ArchiveOrUnarchiveButton({
  archiveButtonClass: ArchiveButtonClass = ArchiveButton,
  archiveTooltipTitle,
  unarchiveTooltipTitle,
  buttonStyle,
  fullWidth = false,
  className,
  isArchivable = false,
  isUnarchivable = false,
  item,
  onArchive,
  onUnarchive,
  showText = false,
  getAnalyticsMetadata,
}: ArchiveOrUnarchiveButtonProps): JSX.Element | null {
  const sharedButtonProps = {
    className,
    fullWidth,
    showText,
    getAnalyticsMetadata,
  };
  if (isArchived(item) && isUnarchivable) {
    return (
      <UnarchiveButton
        {...sharedButtonProps}
        buttonStyle={buttonStyle || ButtonStyle.SECONDARY}
        itemIds={[item.id]}
        onUnarchive={onUnarchive}
        unarchiveOptions={
          APIUtil.isBiologicalEntity(item.id)
            ? {
                hasRegisteredItems: item.isRegistered || FileUtil.isInRegistry(item),
              }
            : undefined
        }
        tooltipTitle={unarchiveTooltipTitle}
      />
    );
  }
  if (!isArchived(item) && coreObjectIsArchivable(item) && isArchivable) {
    return (
      <ArchiveButtonClass
        {...sharedButtonProps}
        buttonStyle={buttonStyle || ButtonStyle.NEGATIVE}
        items={[item]}
        onArchive={onArchive}
        tooltipTitle={archiveTooltipTitle}
      />
    );
  }
  return null;
}
